import React from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import './src/styles.scss';
import GlobalContextWrapper from './src/context/GlobalContext';
import Layout from './src/components/layout/Layout';
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/600.css';
import 'bootstrap/js/dist/collapse';
import { initializeCapacitor } from './src/services/capacitorInit';
import { isCapacitorBuild } from './src/utils/platform';
import { errorReporter } from './src/services/errorReporter';
import { AppErrorBoundary } from './src/components/AppErrorBoundary';
// configure axios for all API calls
axios.defaults.baseURL = process.env.GATSBY_API_URL || '';

// Initialize error reporter before anything else so early failures are captured.
errorReporter.init().catch((err) => {
  console.error('errorReporter init failed:', err);
});

// Initialize Capacitor native plugins (no-op on web)
initializeCapacitor().catch((err) => {
  errorReporter.report(err, 'critical').catch(() => undefined);
  console.error('Capacitor initialization failed:', err);
});

// For Capacitor builds, bypass hydration entirely. The bundled index.html
// was pre-rendered at build time with no auth state; on device, the app
// restores a JWT from localStorage before first render — producing an
// unavoidable SSR/client mismatch (React errors #418/#423) that blanks
// the page. createRoot skips hydration and renders client-side from
// scratch. Native splash covers the first-paint.
export const replaceHydrateFunction = () => {
  if (!isCapacitorBuild) {
    return undefined; // web: use default hydrateRoot (preserves SEO)
  }
  return (
    element: React.ReactElement,
    container: Element | DocumentFragment
  ) => {
    const root = createRoot(container);
    root.render(element);
    // Match Gatsby's default renderer: return an unmount function so
    // Gatsby internals (HMR, teardown) can dispose the root cleanly.
    return () => root.unmount();
  };
};

export const onRouteUpdate = ({ location }: { location?: Location }) => {
  if (location?.pathname) errorReporter.setRoute(location.pathname);
};

// Gatsby also runs wrapRootElement on each page's Head export, rendering it
// into a separate hidden React root. Head elements always receive Gatsby's
// Head props (location, pageContext, ...) while the site root element has
// none. Head only renders <title>/<meta> tags and reads no app context, so it
// skips the providers — otherwise every provider mounts a second time and
// repeats its API calls.
const isHeadElement = (element: React.ReactNode) =>
  React.isValidElement(element) &&
  'pageContext' in (element.props as Record<string, unknown>);

export const wrapRootElement = ({ element }: { element: React.ReactNode }) => {
  if (isHeadElement(element)) {
    return element;
  }

  return (
    <AppErrorBoundary>
      <GlobalContextWrapper>{element}</GlobalContextWrapper>
    </AppErrorBoundary>
  );
};

export const wrapPageElement = ({
  element,
  props,
}: {
  element: React.ReactNode;
  props: { location: Location };
}) => {
  return (
    <Layout pathname={props.location.pathname} search={props.location.search}>
      {element}
    </Layout>
  );
};
